import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Separator } from 'app/components/ui/separator';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from 'app/components/ui/card';
import { User, Save, RefreshCw, Upload, X } from 'lucide-react';
import { useProfileSlice } from '../slice';
import { useToast } from 'app/components/ui/use-toast';
import { useMediaSlice } from 'app/pages/Media/slice';
import { endpoints } from 'utils/api/endpoints';

export function ProfileTab() {
  const form = useForm();

  const { toast } = useToast();

  const { useGetProfileQuery, useUpdateProfileMutation } = useProfileSlice();
  const [getProfile, { data: profile, isLoading, isError }] =
    useGetProfileQuery();
  const [
    updateProfile,
    {
      isLoading: isUpdating,
      isError: isUpdateError,
      error: updateError,
      data: updateData,
    },
  ] = useUpdateProfileMutation();
  // Avatar upload state and ref
  const { useUploadMediaMutation } = useMediaSlice();
  const [uploadMedia] = useUploadMediaMutation();
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      const data = profile.data;
      form.reset({
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        email: data.email,
        avatar: data.avatar,
      });
    }
  }, [profile, form]);

  const onSubmit = (data: any) => {
    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      avatar: data.avatar,
    };
    updateProfile(payload);
    toast({
      description: 'Your profile has been updated successfully.',
      variant: 'success',
    });
  };

  useEffect(() => {
    if (isUpdateError) {
      toast({
        description: (updateError as any).data.message,
        variant: 'destructive',
      });
    }
  }, [isUpdateError, updateError]);

  useEffect(() => {
    if (updateData) {
      toast({
        description: updateData.message,
        variant: 'success',
      });
    }
  }, [updateData]);

  // Avatar upload handler
  const handleUploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('users', file); // or 'avatar' if that's the correct key
      setIsUploadingAvatar(true);
      try {
        const res = await uploadMedia(formData).unwrap();
        // Use the correct response key for avatar
        const uploaded = res.response?.users?.[0] || res.response?.avatar?.[0];
        form.setValue('avatar', uploaded);
      } catch (error) {
        console.error('Avatar upload failed:', error);
      } finally {
        setIsUploadingAvatar(false);
        if (avatarInputRef.current) avatarInputRef.current.value = '';
      }
    }
  };

  // Remove avatar handler
  const handleRemoveAvatar = () => {
    form.setValue('avatar', null);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
          <CardDescription>
            Update your personal information and profile picture.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
              {form.watch('avatar') ? (
                <img
                  src={`${endpoints.mediaBaseUrl}/users/${form.watch(
                    'avatar',
                  )}`}
                  alt="Avatar"
                  className="w-20 h-20 object-cover rounded-full"
                />
              ) : (
                <User className="w-10 h-10 text-gray-500" />
              )}
            </div>
            <div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() =>
                    !isUploadingAvatar &&
                    avatarInputRef.current &&
                    avatarInputRef.current.click()
                  }
                  disabled={isUploadingAvatar}
                >
                  {isUploadingAvatar ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-600" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload Photo
                </Button>
                {form.watch('avatar') && !isUploadingAvatar && (
                  <Button
                    variant="outline"
                    size="icon"
                    type="button"
                    onClick={handleRemoveAvatar}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={avatarInputRef}
                  className="hidden"
                  onChange={handleUploadAvatar}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG or GIF. Max size 2MB.
              </p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                {...form.register('first_name')}
                placeholder="Enter your first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                {...form.register('last_name')}
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              {...form.register('email')}
              placeholder="Enter your email address"
              disabled
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              {...form.register('phone')}
              placeholder="Enter your phone number"
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting || isUpdating}
            >
              {form.formState.isSubmitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
